class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Preserve the free shipping calculator before replacing HTML
    const drawerInner = this.querySelector('.drawer__inner');
    const calculator = drawerInner ? drawerInner.querySelector('.free-shipping-calculator') : null;

    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // Restore the calculator if we had one
    if (calculator) {
      // Get the NEW drawer inner after HTML replacement (old one was removed from DOM)
      const newDrawerInner = this.querySelector('.drawer__inner');
      const newCalculatorPlaceholder = newDrawerInner ? newDrawerInner.querySelector('.free-shipping-calculator') : null;

      if (newCalculatorPlaceholder) {
        newCalculatorPlaceholder.parentNode.replaceChild(calculator, newCalculatorPlaceholder);

        // Update the calculator with new cart data
        setTimeout(() => {
          calculator.setAttribute('data-cart-total', parsedState.total_price);
          if (parsedState.total_weight !== undefined) {
            calculator.setAttribute('data-cart-weight', (parsedState.total_weight / 1000).toFixed(1));
          }

          // Trigger calculator update
          const cartUpdateEvent = new CustomEvent('cart-update-preserved', {
            detail: { cart: parsedState }
          });
          calculator.dispatchEvent(cartUpdateEvent);
        }, 50);
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Wait for CartItems to be defined before extending it
function defineCartDrawerItems() {
  if (typeof CartItems === 'undefined') {
    // If CartItems is not defined yet, wait a bit and try again
    setTimeout(defineCartDrawerItems, 10);
    return;
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
          excludeCalculator: true // Flag to indicate we want to preserve the calculator
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    updateCartDrawerManually(parsedState, section) {
      console.log('[Cart Update] Manually updating cart drawer while preserving calculator');

      // Get the cart drawer element
      const cartDrawer = document.getElementById(section.id);
      if (!cartDrawer) {
        console.error('[Cart Update] Cart drawer element not found');
        return;
      }

      // Get the updated HTML from the server response
      const updatedHtml = parsedState.sections[section.section];
      if (!updatedHtml) {
        console.error('[Cart Update] No updated HTML found for cart drawer');
        return;
      }

      // Parse the updated HTML
      const parser = new DOMParser();
      const updatedDoc = parser.parseFromString(updatedHtml, 'text/html');
      const updatedDrawerInner = updatedDoc.querySelector(section.selector);

      if (!updatedDrawerInner) {
        console.error('[Cart Update] Updated drawer inner element not found');
        return;
      }

      // Find the current drawer inner
      const currentDrawerInner = cartDrawer.querySelector(section.selector);
      if (!currentDrawerInner) {
        console.error('[Cart Update] Current drawer inner element not found');
        return;
      }

      // Find the calculator element and preserve it
      const calculator = currentDrawerInner.querySelector('.free-shipping-calculator');
      console.log('[Cart Update] Found calculator to preserve:', !!calculator);

      // Preserve the footer classes (for FBT border logic)
      const footerElement = currentDrawerInner.querySelector('.cart-drawer-items-footer');
      const footerClasses = footerElement ? footerElement.className : '';
      console.log('[Cart Update] Preserving footer classes:', footerClasses);

      // Replace the entire drawer inner HTML
      currentDrawerInner.innerHTML = updatedDrawerInner.innerHTML;

      // If we had a calculator, restore it
      if (calculator) {
        const newCalculatorPlaceholder = currentDrawerInner.querySelector('.free-shipping-calculator');
        if (newCalculatorPlaceholder) {
          console.log('[Cart Update] Restoring preserved calculator');
          newCalculatorPlaceholder.parentNode.replaceChild(calculator, newCalculatorPlaceholder);
        } else {
          console.log('[Cart Update] No calculator placeholder found in updated HTML, appending calculator');
          // Find where to insert the calculator (look for cart items or footer)
          const cartItems = currentDrawerInner.querySelector('#CartDrawer-CartItems');
          if (cartItems) {
            cartItems.parentNode.insertBefore(calculator, cartItems.nextSibling);
          }
        }

        // Update the calculator with new cart data
        console.log('[Cart Update] Updating calculator with new cart data:', parsedState.total_price);
        setTimeout(() => {
          // Update the calculator's data attributes with new cart info
          calculator.setAttribute('data-cart-total', parsedState.total_price);
          calculator.setAttribute('data-cart-weight', (parsedState.total_weight / 1000).toFixed(1));

          // Trigger calculator update by dispatching cart update event
          const cartUpdateEvent = new CustomEvent('cart-update-preserved', {
            detail: { cart: parsedState }
          });
          calculator.dispatchEvent(cartUpdateEvent);

          // Also try the standard cart update event
          if (window.PubSub && window.PubSub.publish) {
            window.PubSub.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-manual-update' });
          }
        }, 50);
      }

      // Restore footer classes after HTML replacement
      const updatedFooterElement = currentDrawerInner.querySelector('.cart-drawer-items-footer');
      if (updatedFooterElement && footerClasses) {
        console.log('[Cart Update] Restoring footer classes:', footerClasses);
        updatedFooterElement.className = footerClasses;
      }

      // Update the drawer empty state
      const drawerWrapper = document.querySelector('cart-drawer');
      if (drawerWrapper) {
        drawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        currentDrawerInner.classList.toggle('is-empty', parsedState.item_count === 0);
      }

      console.log('[Cart Update] Cart drawer manually updated, calculator preserved');

      // Update footer border after manual cart drawer update
      setTimeout(() => {
        const fbtSection = currentDrawerInner.querySelector('.cart-drawer-frequently-bought');
        const footer = currentDrawerInner.querySelector('.cart-drawer-items-footer');

        if (footer) {
          const hasFBT = fbtSection && fbtSection.querySelectorAll('._21e-fbt-product').length > 0;
          console.log('[Cart Update] Updating footer border after manual update - has FBT:', hasFBT);

          if (hasFBT) {
            footer.classList.add('has-fbt');
          } else {
            footer.classList.remove('has-fbt');
          }

          footer.setAttribute('data-js-has-fbt', hasFBT ? 'true' : 'false');
          const fbtProducts = fbtSection ? fbtSection.querySelectorAll('._21e-fbt-product') : [];
          footer.setAttribute('data-js-fbt-count', fbtProducts.length);
        }
      }, 100);
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

// Try to define immediately, or wait if CartItems isn't loaded yet
defineCartDrawerItems();
